import { io, type Socket } from 'socket.io-client';
import type { Conversation, Message } from '@/types/domain';
import { toConversation, toMessage } from './chatService';
import { signalBadgesChanged } from './badgeSignal';
import {
  toNotificationItem,
  type ApiNotificationItem,
  type NotificationItem,
} from './notificationService';

/**
 * The single socket for the whole app — screens never import socket.io-client.
 *
 * NOTIFY ONLY. Nothing is ever sent through here except room join/leave;
 * messages go over REST. So if this file never connects,
 * chat still works — the screens' polling fallback covers it, and that fallback
 * always runs rather than being a failure branch nobody exercises.
 *
 * Transport note: socket.io tries HTTP long-polling first and upgrades to
 * WebSocket only if the network allows. If the Swarm proxy in front of the API
 * blocks the upgrade, this keeps working over plain HTTP.
 */

const NAMESPACE = '/chat';

/** Same base the REST client uses; '' means same-origin (dev proxy or prod). */
const socketOrigin = (): string => {
  const base = import.meta.env.VITE_API_BASE_URL as string | undefined;
  if (!base || base.startsWith('/')) return '';
  try {
    return new URL(base).origin;
  } catch {
    return '';
  }
};

export interface ChatSocketHandlers {
  onMessage?: (message: Message) => void;
  onConversationUpdated?: (conversation: Conversation) => void;
  onRead?: (payload: { conversationId: string; readerId: string }) => void;
  onNotification?: (event: { item: NotificationItem; unreadCount: number }) => void;
}

let socket: Socket | null = null;
let refCount = 0;
const handlerSets = new Set<ChatSocketHandlers>();

const dispatch = <K extends keyof ChatSocketHandlers>(
  key: K,
  invoke: (handlers: ChatSocketHandlers) => void
) => {
  for (const handlers of handlerSets) {
    if (handlers[key]) invoke(handlers);
  }
};

const ensureSocket = (): Socket => {
  if (socket) return socket;

  socket = io(`${socketOrigin()}${NAMESPACE}`, {
    // The JWT rides the same httpOnly auth_token cookie as every REST call —
    // no second token to keep in sync, and nothing readable from JS.
    withCredentials: true,
    // A failed connection is not an error state for the app; polling covers it.
    reconnectionDelay: 1000,
    reconnectionDelayMax: 10000,
  });

  socket.on('message:new', (raw: Parameters<typeof toMessage>[0]) => {
    const message = toMessage(raw);
    dispatch('onMessage', (h) => h.onMessage!(message));
  });

  socket.on('conversation:updated', (raw: Parameters<typeof toConversation>[0]) => {
    const conversation = toConversation(raw);
    dispatch('onConversationUpdated', (h) => h.onConversationUpdated!(conversation));
  });

  socket.on('message:read', (raw: { conversationId: number; readerId: number }) => {
    const payload = {
      conversationId: String(raw.conversationId),
      readerId: String(raw.readerId),
    };
    dispatch('onRead', (h) => h.onRead!(payload));
  });

  socket.on('notification:new', (raw: { item: ApiNotificationItem; unreadCount: number }) => {
    const event = { item: toNotificationItem(raw.item), unreadCount: raw.unreadCount };
    dispatch('onNotification', (h) => h.onNotification?.(event));
  });

  // Not a handler: badges listen on the app-wide signal, which this tab's own reads feed too.
  socket.on('badges:changed', (raw: { removedIds?: number[] }) => {
    signalBadgesChanged({ removedIds: raw.removedIds?.map(String) });
  });

  return socket;
};

/**
 * Subscribe. Returns an unsubscribe function; the socket disconnects once the
 * last subscriber leaves, so a guest browsing the map holds no connection.
 */
export const subscribeChatSocket = (handlers: ChatSocketHandlers): (() => void) => {
  const active = ensureSocket();
  handlerSets.add(handlers);
  refCount += 1;

  return () => {
    handlerSets.delete(handlers);
    refCount -= 1;
    if (refCount <= 0) {
      refCount = 0;
      active.disconnect();
      socket = null;
    }
  };
};

/** Join/leave the room for an open thread. No-ops when the socket is down. */
export const joinConversation = (conversationId: string): void => {
  socket?.emit('conversation:join', Number(conversationId));
};

export const leaveConversation = (conversationId: string): void => {
  socket?.emit('conversation:leave', Number(conversationId));
};
